import React, { useContext } from 'react'
import { array, bool } from 'prop-types'

import Grid from '../../Grid'
import Card from '../../Card'
import Carousel from '../../Carousel'
import Hide from '../../Hide'
import Surround from '../../Surround'
import Tag from '../../Tag'
import CardButton from '../../Card/component/CardButton'
import { ThemeContext } from '@/component/Context/ThemeContext'

import brandToColor from '@/lib/brand-to-color'
import Blockquote from '../../Blockquote'
import { RowContext } from '@/component/Context/RowContext'

const CardGrid2 = ({ articles }) => {
  const { theme } = useContext(ThemeContext)
  const inverseRow = useContext(RowContext)
  if (!articles.length) return null
  const themecolor = brandToColor(theme)
  const gridArticles = articles.slice(2)
  const hasFeaturedArticle = articles[0].featured
  const hasQuote = articles[1] && articles[1].quote
  const imageRatio = 455 / 322

  return (
    <>
      <Hide below="desktop">
        <Grid flex gutter="default">
          <Grid.Item
            trailingGap={inverseRow ? 'desktop' : null}
            width={{ d: 1 / 3 }}
          >
            <Card
              typeConfig={{
                title: {
                  size: 'heading3',
                  themed: true
                },
                subtitle: {
                  size: 'heading6'
                },
                description: { size: 'body2' }
              }}
              inverse={hasFeaturedArticle || inverseRow}
              inset={hasFeaturedArticle && !inverseRow}
              headerComponents={
                hasFeaturedArticle
                  ? () => <Tag background="white">Featured</Tag>
                  : undefined
              }
              footerComponents={() => (
                <CardButton inverse={inverseRow}>
                  {articles[0].ctaText}
                </CardButton>
              )}
              {...{
                ...articles[0],
                sizes: [
                  '(min-width: 1420px) 290px',
                  '(min-width: 960px) calc(25vw - 32px - 80px)',
                  'calc((100vw - 80px) * 0.5)'
                ],
                width: 290
              }}
              imageRatio={imageRatio}
            />
          </Grid.Item>
          <Grid.Item width={{ d: 2 / 3 }}>
            <Grid flex gutter="default">
              {articles[1] && (
                <Grid.Item trailingGap="default">
                  <Grid gutter="default" flex>
                    <Grid.Item
                      trailingGap="mobile"
                      width={hasQuote && { d: 2 / 3 }}
                    >
                      <Surround padded="desktop" bordered="desktop">
                        <Card
                          typeConfig={{
                            title: { size: 'heading6', themed: true },
                            subtitle: {
                              size: ['body2', 'body3'],
                              color: themecolor,
                              themed: true
                            }
                          }}
                          horizontal
                          inverse={inverseRow}
                          gridGutter="default"
                          gridWidths={
                            hasQuote ? [1 / 3, 2 / 3] : [1 / 4, 3 / 4]
                          }
                          footerComponents={() => (
                            <CardButton inverse={inverseRow}>
                              {articles[1].ctaText}
                            </CardButton>
                          )}
                          {...{
                            ...articles[1],
                            sizes: [
                              '(min-width: 1420px) 206px',
                              '(min-width: 960px) 104px',
                              'calc(75vw - 80px)'
                            ],
                            width: 206
                          }}
                          imageRatio={imageRatio}
                        />
                      </Surround>
                    </Grid.Item>
                    {hasQuote && (
                      <Grid.Item width={{ d: 1 / 3 }}>
                        <Blockquote
                          attribution={articles[1].attribution}
                          secondaryAttribution={articles[1].subAttribution}
                          inverse={inverseRow}
                        >
                          {articles[1].quote}
                        </Blockquote>
                      </Grid.Item>
                    )}
                  </Grid>
                </Grid.Item>
              )}
              {gridArticles.map((article, i) => (
                <Grid.Item
                  key={i}
                  trailingGap={hasFeaturedArticle ? 'desktop' : null}
                  width={{ d: 1 / 3 }}
                >
                  <Card
                    typeConfig={{
                      title: { size: 'heading6', themed: true },
                      subtitle: {
                        size: ['body2', 'body3'],
                        color: themecolor,
                        themed: true
                      }
                    }}
                    footerComponents={() => (
                      <CardButton inverse={inverseRow}>
                        {article.ctaText}
                      </CardButton>
                    )}
                    inverse={inverseRow}
                    {...{
                      ...article,
                      sizes: [
                        '(min-width: 1420px) 206px',
                        'calc(16.6666vw - 80px)'
                      ],
                      width: 206
                    }}
                    imageRatio={imageRatio}
                  />
                </Grid.Item>
              ))}
            </Grid>
          </Grid.Item>
        </Grid>
      </Hide>
      <Hide at="desktop">
        <Grid>
          <Grid.Item trailingGap="mobile">
            <Card
              typeConfig={{
                title: { size: 'heading5' },
                subtitle: { color: themecolor }
              }}
              inverse={articles[0].featured || inverseRow}
              inset={articles[0].featured}
              footerComponents={() => (
                <CardButton inverse={inverseRow}>
                  {articles[0].ctaText}
                </CardButton>
              )}
              {...{
                ...articles[0],
                sizes: ['calc((100vw - 80px) * 0.5)'],
                width: 550
              }}
              imageRatio={imageRatio}
            />
          </Grid.Item>
          <Grid.Item trailingGap="mobile">
            <Carousel>
              {articles.slice(1).map((article, i) => (
                <div key={i}>
                  <Card
                    typeConfig={{
                      title: { size: 'heading7' },
                      subtitle: { size: ['body2', 'body3'], color: themecolor }
                    }}
                    inverse={inverseRow}
                    footerComponents={() => (
                      <CardButton inverse={inverseRow}>
                        {article.ctaText}
                      </CardButton>
                    )}
                    {...{
                      ...article,
                      sizes: ['calc(75vw - 80px)'],
                      width: 412
                    }}
                    imageRatio={imageRatio}
                  />
                </div>
              ))}
            </Carousel>
          </Grid.Item>
        </Grid>
      </Hide>
    </>
  )
}

CardGrid2.propTypes = {
  articles: array,
  hasFeaturedArticle: bool
}

export default CardGrid2
